import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '../context/AuthContext';
import { AuthSettingsProvider } from '../context/AuthSettingsContext';
import { ProtectedRoute } from './ProtectedRoute';
import { Layout } from '../components/layout/Layout';
import { Login } from '../pages/Login';
import { MicrosoftCallback } from '../pages/MicrosoftCallback';
import { PageSkeleton } from '../components/common/LoadingSpinner';

// Lazy load pages for better performance
const StaffingTable = lazy(() => import('../pages/StaffingTable').then(m => ({ default: m.StaffingTable })));
const SaisieStaffing = lazy(() => import('../pages/SaisieStaffing').then(m => ({ default: m.SaisieStaffing })));
const Clients = lazy(() => import('../pages/Clients').then(m => ({ default: m.Clients })));
const Projects = lazy(() => import('../pages/Projects').then(m => ({ default: m.Projects })));
const Consultants = lazy(() => import('../pages/Consultants').then(m => ({ default: m.Consultants })));
const Teams = lazy(() => import('../pages/Teams').then(m => ({ default: m.Teams })));
const UserManagement = lazy(() => import('../pages/UserManagement').then(m => ({ default: m.UserManagement })));
const AdminSettings = lazy(() => import('../pages/AdminSettings').then(m => ({ default: m.AdminSettings })));

const Unauthorized = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-gray-900">Accès non autorisé</h2>
      <p className="text-gray-500 mt-2">Vous n'avez pas les permissions pour accéder à cette page.</p>
    </div>
  </div>
);

export function AppRouter() {
  return (
    <AuthProvider>
      <AuthSettingsProvider>
        <Router>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/ms/callback" element={<MicrosoftCallback />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            {/* Home = the staffing dashboard, served at the root URL (no redirect) */}
            <Route index element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin', 'consultant']}>
                  <StaffingTable />
                </ProtectedRoute>
              </Suspense>
            } />
            
            {/* Staffing section */}
            <Route path="staffing" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin', 'consultant']}>
                  <StaffingTable />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="saisie-staffing" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin']}>
                  <SaisieStaffing />
                </ProtectedRoute>
              </Suspense>
            } />
            
            {/* Database section */}
            <Route path="clients" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin']}>
                  <Clients />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="projects" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin']}>
                  <Projects />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="consultants" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin']}>
                  <Consultants />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="teams" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['manager', 'superadmin']}>
                  <Teams />
                </ProtectedRoute>
              </Suspense>
            } />
            
            {/* Administration section */}
            <Route path="users" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['superadmin']}>
                  <UserManagement />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route path="admin-settings" element={
              <Suspense fallback={<PageSkeleton />}>
                <ProtectedRoute roles={['superadmin']}>
                  <AdminSettings />
                </ProtectedRoute>
              </Suspense>
            } />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/staffing" replace />} />
          </Routes>
        </Router>
      </AuthSettingsProvider>
    </AuthProvider>
  );
}